import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, errorMessage, publicApi, unwrap } from "../api";
import { LegalLayout } from "./PrivacyPage";

/**
 * Public: the confirmation link emailed to a new emergency contact. Nothing
 * changes until they press a button (mail scanners only load the page).
 */
export function EmergencyContactPage() {
  const { token = "" } = useParams();
  const queryClient = useQueryClient();
  const key = ["contact-invitation", token];
  const invitation = useQuery({
    queryKey: key,
    queryFn: async () => unwrap(await publicApi.GET("/contact-invitations/{token}", { params: { path: { token } } })),
    retry: false,
  });
  const [declined, setDeclined] = useState(false);
  const accept = useMutation({
    mutationFn: async () =>
      unwrap(await publicApi.POST("/contact-invitations/{token}/accept", { params: { path: { token } } })),
    onSuccess: (updated) => queryClient.setQueryData(key, updated),
  });
  const decline = useMutation({
    mutationFn: async () => {
      const result = await publicApi.POST("/contact-invitations/{token}/decline", { params: { path: { token } } });
      if (!result.response.ok) unwrap({ ...result, data: undefined });
    },
    onSuccess: () => setDeclined(true),
  });
  const gone = invitation.error instanceof ApiError && invitation.error.status === 404;
  const data = invitation.data;
  const busy = accept.isPending || decline.isPending;
  const failure = accept.error ?? decline.error ?? (gone ? null : invitation.error);
  return (
    <LegalLayout title="Emergency contact">
      {invitation.isPending && <p className="muted">Loading…</p>}
      {gone && !declined && (
        <p className="muted">This link has expired or was replaced. Ask them to send you a new one.</p>
      )}
      {declined ? (
        <p role="status">Done. You are not an emergency contact, and we won&apos;t email you about them.</p>
      ) : (
        data && (
          <>
            {data.confirmed ? (
              <p role="status">
                You are one of <strong>{data.user_name}</strong>&apos;s emergency contacts. If they press SOS in ten20,
                you will get an email with a link to their location.
              </p>
            ) : (
              <>
                <p>
                  Hi {data.contact_name}, <strong>{data.user_name}</strong> asked you to be an emergency contact on
                  ten20, a family location-sharing app.
                </p>
                <p>
                  If they press SOS, you will get an email with a link to a page showing where they are until they say
                  they are safe. You won&apos;t get any other email from us, and you don&apos;t need an account.
                </p>
              </>
            )}
            <div className="button-row">
              {!data.confirmed && (
                <button type="button" disabled={busy} onClick={() => accept.mutate()}>
                  Accept
                </button>
              )}
              <button type="button" className="secondary" disabled={busy} onClick={() => decline.mutate()}>
                {data.confirmed ? "Stop being their emergency contact" : "Decline"}
              </button>
            </div>
          </>
        )
      )}
      {failure && (
        <p className="error" role="alert">
          {errorMessage(failure, { 404: "This link has expired or was replaced." })}
        </p>
      )}
    </LegalLayout>
  );
}
